
import axios from 'axios';
import { RoomStatus } from '../types/enums';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Secure Operating Room Service
 * Uses backend API endpoints instead of direct Firestore access
 */

/**
 * Get all operating rooms for a specific facility
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Array>} Array of operating rooms with status information
 */
export const getOperatingRoomsForFacility = async (facilityId) => {
  try {
    console.log('Fetching operating rooms for facility:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/surgical/operating-rooms?facilityId=${facilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log(`Found ${response.data.operatingRooms.length} operating rooms for facility ${facilityId}`);
    return response.data.operatingRooms;
  } catch (error) {
    console.error('Error getting operating rooms for facility:', error);
    throw error;
  }
};

/**
 * Get operating room by ID
 * @param {string} facilityId - The facility ID
 * @param {string} roomId - The room ID
 * @returns {Promise<Object>} Operating room object
 */
export const getOperatingRoomById = async (facilityId, roomId) => {
  try {
    console.log('Fetching operating room:', roomId, 'for facility:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/surgical/operating-rooms/${roomId}?facilityId=${facilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.operatingRoom;
  } catch (error) {
    console.error('Error getting operating room by ID:', error);
    throw error;
  }
};

/**
 * Get rooms by room type for a specific facility
 * @param {string} facilityId - The facility ID
 * @param {string} roomType - The room type to filter by
 * @returns {Promise<Array>} Array of rooms with the specified type
 */
export const getRoomsByType = async (facilityId, roomType) => {
  try {
    console.log('Fetching rooms by type:', roomType, 'for facility:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/surgical/rooms-by-type?facilityId=${facilityId}&roomType=${encodeURIComponent(roomType)}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log(`Found ${response.data.rooms.length} rooms of type ${roomType} for facility ${facilityId}`);
    return response.data.rooms;
  } catch (error) {
    console.error('Error getting rooms by type:', error);
    throw error;
  }
};

/**
 * Update operating room status
 * @param {string} facilityId - The facility ID
 * @param {string} roomId - The room ID
 * @param {string} newStatus - New status for the room
 * @param {string} departmentId - Department ID where the room is located
 * @returns {Promise<Object>} Update result
 */
export const updateOperatingRoomStatus = async (facilityId, roomId, newStatus, departmentId) => {
  try {
    console.log('Updating operating room status:', roomId, 'to:', newStatus);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/surgical/operating-rooms/${roomId}/status`,
      {
        facilityId,
        status: newStatus,
        departmentId
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Operating room status updated successfully');
    return response.data;
  } catch (error) {
    console.error('Error updating operating room status:', error);
    throw error;
  }
};

/**
 * Get operating room availability for a date range
 * @param {string} facilityId - The facility ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Promise<Object>} Availability data
 */
export const getOperatingRoomAvailability = async (facilityId, startDate, endDate) => {
  try {
    console.log('Fetching operating room availability for facility:', facilityId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('startDate', startDate.toISOString());
    params.append('endDate', endDate.toISOString());
    
    const response = await axios.get(
      `${API_BASE}/surgical/operating-rooms/availability?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.availability;
  } catch (error) {
    console.error('Error getting operating room availability:', error);
    throw error;
  }
};

/**
 * Update room data in the facilities collection
 * @param {string} facilityId - The facility ID
 * @param {string} departmentId - The department ID where the room is located
 * @param {string} roomCode - The room code/ID to update
 * @param {Object} roomData - The room data to update
 * @returns {Promise<Object>} Update result
 */
export const updateRoomInFacility = async (facilityId, departmentId, roomCode, roomData) => {
  try {
    console.log('Updating room in facility:', roomCode, 'for facility:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/facility/facilities/${facilityId}/departments/${departmentId}/rooms/${roomCode}`,
      roomData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Room updated successfully in facility');
    return response.data;
  } catch (error) {
    console.error('Error updating room in facility:', error);
    throw error;
  }
};

/**
 * Delete room from the facilities collection
 * @param {string} facilityId - The facility ID
 * @param {string} departmentId - The department ID where the room is located
 * @param {string} roomCode - The room code/ID to delete
 * @returns {Promise<Object>} Delete result
 */
export const deleteRoomFromFacility = async (facilityId, departmentId, roomCode) => {
  try {
    console.log('Deleting room from facility:', roomCode, 'for facility:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.delete(
      `${API_BASE}/facility/facilities/${facilityId}/departments/${departmentId}/rooms/${roomCode}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Room deleted successfully from facility');
    return response.data;
  } catch (error) {
    console.error('Error deleting room from facility:', error);
    throw error;
  }
};

/**
 * Schedule a surgical case in an operating room
 * @param {string} facilityId - The facility ID
 * @param {string} roomId - The room ID
 * @param {Object} caseData - Case data including patient, surgeon, etc.
 * @returns {Promise<Object>} Update result
 */
export const scheduleOperatingRoomCase = async (facilityId, roomId, caseData) => {
  try {
    console.log('Scheduling case in operating room:', roomId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/surgical/operating-rooms/${roomId}/schedule`,
      {
        facilityId,
        ...caseData
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Operating room case scheduled successfully');
    return response.data;
  } catch (error) {
    console.error('Error scheduling operating room case:', error);
    throw error;
  }
};

/**
 * Get current and upcoming cases for an operating room
 * @param {string} facilityId - The facility ID
 * @param {string} roomId - The room ID
 * @returns {Promise<Object>} Current and upcoming cases
 */
export const getOperatingRoomSchedule = async (facilityId, roomId) => {
  try {
    console.log('Fetching operating room schedule:', roomId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/surgical/operating-rooms/${roomId}/schedule?facilityId=${facilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.schedule;
  } catch (error) {
    console.error('Error getting operating room schedule:', error);
    throw error;
  }
};

/**
 * Update operating room equipment
 * @param {string} facilityId - The facility ID
 * @param {string} roomId - The room ID
 * @param {string} departmentId - Department ID
 * @param {Array} equipment - Equipment list
 * @returns {Promise<Object>} Update result
 */
export const updateOperatingRoomEquipment = async (facilityId, roomId, departmentId, equipment) => {
  try {
    console.log('Updating operating room equipment:', roomId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/surgical/operating-rooms/${roomId}/equipment`,
      {
        facilityId,
        departmentId,
        equipment
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Operating room equipment updated successfully');
    return response.data;
  } catch (error) {
    console.error('Error updating operating room equipment:', error);
    throw error;
  }
};

/**
 * Get operating room utilization statistics
 * @param {string} facilityId - The facility ID
 * @param {Date} startDate - Start date for statistics
 * @param {Date} endDate - End date for statistics
 * @returns {Promise<Object>} Utilization statistics
 */
export const getOperatingRoomUtilization = async (facilityId, startDate, endDate) => {
  try {
    console.log('Fetching operating room utilization for facility:', facilityId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('startDate', startDate.toISOString());
    params.append('endDate', endDate.toISOString());
    
    const response = await axios.get(
      `${API_BASE}/surgical/operating-rooms/utilization?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.utilization;
  } catch (error) {
    console.error('Error getting operating room utilization:', error);
    throw error;
  }
};

/**
 * Get available operating rooms for a specific time slot
 * @param {string} facilityId - The facility ID
 * @param {Date} startTime - Start time
 * @param {Date} endTime - End time
 * @param {Array} requiredSpecialties - Required specialties
 * @returns {Promise<Array>} Available operating rooms
 */
export const getAvailableOperatingRooms = async (facilityId, startTime, endTime, requiredSpecialties = []) => {
  try {
    console.log('Fetching available operating rooms for time slot');
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('startTime', startTime.toISOString());
    params.append('endTime', endTime.toISOString());
    
    if (requiredSpecialties.length > 0) {
      requiredSpecialties.forEach(specialty => {
        params.append('specialties', specialty);
      });
    }
    
    const response = await axios.get(
      `${API_BASE}/surgical/operating-rooms/available?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.availableRooms;
  } catch (error) {
    console.error('Error getting available operating rooms:', error);
    throw error;
  }
};

/**
 * Helper function to map room status for backward compatibility
 * @param {string} status - Room status from backend
 * @returns {string} Mapped status
 */
export const mapRoomStatus = (status) => {
  switch (status) {
    case 'available':
      return RoomStatus.AVAILABLE;
    case 'occupied':
      return RoomStatus.OCCUPIED;
    case 'maintenance':
      return RoomStatus.MAINTENANCE;
    case 'cleaning':
      return RoomStatus.CLEANING;
    default:
      return RoomStatus.AVAILABLE;
  }
};

/**
 * Get emergency operating room (for urgent cases)
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} Emergency operating room if available
 */
export const getEmergencyOperatingRoom = async (facilityId) => {
  try {
    console.log('Finding emergency operating room for facility:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/surgical/operating-rooms/emergency?facilityId=${facilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.emergencyRoom;
  } catch (error) {
    console.error('Error getting emergency operating room:', error);
    throw error;
  }
};
